import { useEffect, useState } from 'react';
import type { ClassroomDto } from '../api/types.js';
import { ALL_PLEDGES, DEFAULT_ENABLED_PLEDGE_KEYS } from '../pages/Pledges/pledgesList.js';
import {
  getEnabledPledgeKeys,
  setEnabledPledgeKeys,
} from '../pages/Pledges/enabledPledgesStorage.js';
import { ResetSettingsButton } from './ResetSettingsButton.js';
import styles from './settingsForm.module.css';

export function PledgesPageSettings({ classroom }: { classroom: ClassroomDto }) {
  const [enabled, setEnabled] = useState<Set<string>>(
    () => new Set(getEnabledPledgeKeys(classroom.id)),
  );

  // Re-pull when the classroom changes underneath us.
  useEffect(() => {
    setEnabled(new Set(getEnabledPledgeKeys(classroom.id)));
  }, [classroom.id]);

  const toggle = (key: string) => {
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      const keys = [...next];
      setEnabledPledgeKeys(classroom.id, keys);
      return next;
    });
  };

  const enabledCount = enabled.size;

  const reset = () => {
    setEnabled(new Set(DEFAULT_ENABLED_PLEDGE_KEYS));
    setEnabledPledgeKeys(classroom.id, [...DEFAULT_ENABLED_PLEDGE_KEYS]);
  };

  return (
    <div className={`glass glass--solid ${styles.panel}`}>
      <header className={styles.header}>
        <div className={styles.headerText}>
          <h2 className={styles.heading}>Pledges</h2>
          <p className={styles.description}>
            Pick which pledges show up in the morning-meeting Pledges page. Tap any pledge during
            live mode to advance to the next enabled one.
          </p>
        </div>
        <ResetSettingsButton pageLabel="Pledges" onReset={reset} />
      </header>

      <div className={styles.form}>
        <ul className={styles.list}>
          {ALL_PLEDGES.map((p) => (
            <li key={p.key} className={styles.listItem}>
              <label className={styles.checkboxRow} style={{ flex: 1 }}>
                <input
                  type="checkbox"
                  checked={enabled.has(p.key)}
                  onChange={() => toggle(p.key)}
                />
                <span className={styles.listItemTitle}>{p.title}</span>
              </label>
            </li>
          ))}
        </ul>
        {enabledCount === 0 ? (
          <span className={styles.statusLine + ' ' + styles.statusError}>
            At least one pledge must be enabled, or the Pledges page will be empty.
          </span>
        ) : (
          <span className={styles.statusLine}>
            {enabledCount} of {ALL_PLEDGES.length} enabled.
          </span>
        )}
      </div>
    </div>
  );
}
